
import { NocheLogo } from "@/components/NocheLogo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Sparkles, LogIn, User, Settings } from "lucide-react";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger, 
  DropdownMenuSeparator 
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export const Header = () => {
  const { user, isLoading, signOut, navigateToDashboard } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="glass-card sticky top-0 z-40 w-full border-b border-border/30">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <NocheLogo />
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-3">
            {isLoading ? (
              <Button 
                variant="noche"
                size="sm"
                disabled
                className="min-w-[100px]"
              >
                <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                <span className="hidden sm:inline">Loading...</span>
              </Button>
            ) : !user ? (
              <Button 
                onClick={() => navigate("/auth")}
                variant="noche"
                size="sm"
                className="min-w-[100px]"
              >
                <LogIn className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Sign In</span>
                <span className="sm:hidden">Sign In</span>
              </Button>
            ) : (
              <div className="flex items-center gap-3">
                {/* Dashboard button for mobile */}
                <Button 
                  onClick={() => navigateToDashboard()}
                  variant="premium"
                  size="sm"
                  className="sm:hidden min-w-[44px]"
                >
                  <Sparkles className="h-4 w-4" />
                </Button>

                {/* Dashboard button for desktop */}
                <Button 
                  onClick={() => navigateToDashboard()}
                  variant="premium"
                  size="sm"
                  className="hidden sm:flex min-w-[120px]"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Dashboard
                </Button>

                {/* User menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="relative h-9 w-9 rounded-full ring-2 ring-primary/20 hover:ring-primary/40 transition-all duration-200"
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user?.user_metadata?.avatar_url} />
                        <AvatarFallback className="bg-gradient-to-br from-primary to-secondary text-white text-xs">
                          {user?.email?.charAt(0).toUpperCase() || "U"}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 glass-card">
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium text-foreground">
                        {user?.user_metadata?.display_name || user?.email}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {user?.email}
                      </p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/dashboard/profile")} className="cursor-pointer">
                      <User className="h-4 w-4 mr-2" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/dashboard/settings")} className="cursor-pointer">
                      <Settings className="h-4 w-4 mr-2" />
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => signOut()} 
                      className="cursor-pointer text-destructive focus:text-destructive"
                    >
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
